import { useMemo } from 'react';
import type { Purchase } from '@/services/purchases';
import PurchaseStatusBadge from './PurchaseStatusBadge';

interface AccountsPayableProps {
  purchases: Purchase[];
  loading: boolean;
  onOpen: (purchase: Purchase) => void;
}

interface SupplierBalance {
  name: string;
  count: number;
  total: number;
  paid: number;
  balance: number;
}

export default function AccountsPayable({ purchases, loading, onOpen }: AccountsPayableProps) {
  const active = useMemo(
    () => purchases.filter((p) => p.status !== 'cancelled'),
    [purchases],
  );

  const bySupplier = useMemo<SupplierBalance[]>(() => {
    const map = new Map<string, SupplierBalance>();
    active.forEach((p) => {
      const key = p.supplierName || 'Sin proveedor';
      const entry = map.get(key) ?? { name: key, count: 0, total: 0, paid: 0, balance: 0 };
      entry.count += 1;
      entry.total += p.totalCost;
      entry.paid += p.paidAmount;
      entry.balance += p.balance;
      map.set(key, entry);
    });
    return Array.from(map.values()).sort((a, b) => b.balance - a.balance);
  }, [active]);

  const pendingPurchases = useMemo(
    () => active.filter((p) => p.balance > 0).sort((a, b) => b.balance - a.balance),
    [active],
  );

  const totalPayable = active.reduce((sum, p) => sum + p.balance, 0);
  const suppliersWithDebt = bySupplier.filter((s) => s.balance > 0).length;

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-background-200 py-16 text-center text-foreground-400 text-sm">
        <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando cuentas por pagar...
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-background-200 p-4">
          <p className="text-xs text-foreground-500">Total por pagar</p>
          <p className="text-xl font-bold text-red-600 mt-1">${totalPayable.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-4">
          <p className="text-xs text-foreground-500">Proveedores con saldo</p>
          <p className="text-xl font-bold text-foreground-950 mt-1">{suppliersWithDebt}</p>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-4">
          <p className="text-xs text-foreground-500">Compras con saldo pendiente</p>
          <p className="text-xl font-bold text-foreground-950 mt-1">{pendingPurchases.length}</p>
        </div>
      </div>

      {/* Saldos por proveedor */}
      <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-background-100">
          <p className="text-sm font-semibold text-foreground-900">Saldos por proveedor</p>
          <p className="text-xs text-foreground-500 mt-0.5">
            Lo que se debe a cada proveedor (compras menos abonos registrados).
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-100 text-left bg-background-50">
                <th className="px-5 py-3 text-xs font-medium text-foreground-500">Proveedor</th>
                <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Compras</th>
                <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Total comprado</th>
                <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Abonado</th>
                <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {bySupplier.map((s) => (
                <tr key={s.name} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                  <td className="px-5 py-3 font-medium text-foreground-900">{s.name}</td>
                  <td className="px-5 py-3 text-right text-foreground-600">{s.count}</td>
                  <td className="px-5 py-3 text-right text-foreground-700 whitespace-nowrap">
                    ${s.total.toLocaleString()}
                  </td>
                  <td className="px-5 py-3 text-right text-emerald-600 whitespace-nowrap">
                    ${s.paid.toLocaleString()}
                  </td>
                  <td className="px-5 py-3 text-right font-semibold whitespace-nowrap">
                    <span className={s.balance > 0 ? 'text-red-600' : 'text-foreground-500'}>
                      ${s.balance.toLocaleString()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {bySupplier.length === 0 && (
          <div className="text-center py-10 text-foreground-500 text-sm">
            <i className="ri-wallet-3-line text-3xl block mb-2"></i>
            No hay compras registradas, así que no hay cuentas por pagar.
          </div>
        )}
      </div>

      {/* Compras con saldo */}
      <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-background-100">
          <p className="text-sm font-semibold text-foreground-900">Compras con saldo pendiente</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-100 text-left bg-background-50">
                <th className="px-5 py-3 text-xs font-medium text-foreground-500">Factura</th>
                <th className="px-5 py-3 text-xs font-medium text-foreground-500">Proveedor</th>
                <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Total</th>
                <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Abonado</th>
                <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Saldo</th>
                <th className="px-5 py-3 text-xs font-medium text-foreground-500">Estado</th>
                <th className="px-5 py-3 text-xs font-medium text-foreground-500 w-14"></th>
              </tr>
            </thead>
            <tbody>
              {pendingPurchases.map((p) => (
                <tr key={p.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                  <td className="px-5 py-3 text-foreground-900 font-medium">{p.invoiceNumber || 'Sin factura'}</td>
                  <td className="px-5 py-3 text-foreground-700">{p.supplierName || '—'}</td>
                  <td className="px-5 py-3 text-right text-foreground-700 whitespace-nowrap">
                    ${p.totalCost.toLocaleString()}
                  </td>
                  <td className="px-5 py-3 text-right text-emerald-600 whitespace-nowrap">
                    ${p.paidAmount.toLocaleString()}
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-red-600 whitespace-nowrap">
                    ${p.balance.toLocaleString()}
                  </td>
                  <td className="px-5 py-3">
                    <PurchaseStatusBadge status={p.status} />
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end">
                      <button
                        onClick={() => onOpen(p)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                        title="Ver y abonar"
                      >
                        <i className="ri-eye-line text-foreground-500"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pendingPurchases.length === 0 && (
          <div className="text-center py-10 text-foreground-500 text-sm">
            <i className="ri-check-double-line text-3xl block mb-2"></i>
            ¡Todo al día! No hay compras con saldo pendiente.
          </div>
        )}
      </div>
    </div>
  );
}